import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, Injectable, input, output, signal, Signal, ViewChild} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeId, GruppeMedlemListeModell, GruppeModell, GruppeRessursClient, MedlemsRolleModell, MøteRessursClient, OpprettGruppeInvitasjonSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatOptionModule} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {TranslateModule} from "@ngx-translate/core";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {MedlemInvitasjonSkjemaKomponent, MedlemInvitasjonSkjemaKomponentAvhengigheter, MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN} from "../medlem-invitasjon-skjema/medlem-invitasjon-skjema";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {MatButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {concatMap, filter, Observable, take} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {scrollTilTopp} from "web-ng2/delt/dom/scrollIntoView";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {MedlemImporterDialogKomponent} from "web-ng2/moduler/medlem/importer-medlemmer/modale/medlem-importer-dialog/medlem-importer-dialog";
import {MatDialog} from "@angular/material/dialog";
import {eksistererMedlemMedEpost, eksistererMedlemMedNavn, medlemmerIkkeAnonymisert, ValiderEpostRespons} from "felles-ng2/modell/medlemModellHjelpere";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {fjernAlleFeilFraFormGroup} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";

const debug = initDebugger('MedlemInvitasjonOpprettKomponent');


@Component({
    selector: 'medlem-invitasjon-opprett',
    templateUrl: 'medlem-invitasjon-opprett.html',
    styleUrl: 'medlem-invitasjon-opprett.scss',
    host: {
        '[class.viser-fullt-skjema]': 'viserFulltSkjema()'
    },
    providers: [{ provide: MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN, useExisting: MedlemInvitasjonOpprettKomponent }],
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatIconModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        MatOptionModule,
        MatSelectModule,
        MatSlideToggle,
        MatTooltip,
        MatButton,
        TranslateModule,
        MedlemInvitasjonSkjemaKomponent,
        InlineSkjemaKomponent,
        InlineSkjemaTittelKomponent,
        InlineSkjemaHandlingerKomponent,
        NavigasjonforhindretFlashDirektiv,
        VisSpinnerDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemInvitasjonOpprettKomponent implements MedlemInvitasjonSkjemaKomponentAvhengigheter {

    @ViewChild('skjemaelement') private skjemaelement!: ElementRef<HTMLFormElement>;

    gruppe = input.required<GruppeModell>();
    visFulltSkjemaHeleTiden = input<boolean>(false);
    vedLagret = output<void>();
    vedAvbryt = output<void>();

    skjema = new FormGroup({
        invitasjon: MedlemInvitasjonSkjemaKomponent.opprettSkjema(),
        oppdaterMoetemal: new FormControl(true, {nonNullable: true}),
        skalSignereSomStandard: new FormControl(true, {nonNullable: true}),
        sendInvitasjon: new FormControl(true, {nonNullable: true}),
        sendInnkallingTilMoeter: new FormControl(true, {nonNullable: true})
    });

    lagrer = asynkArbeidPågårSignal();
    viserFulltSkjema: Signal<boolean>;
    antallMoeterDetSkalSendesInnkallingTilMoeter = signal(0);

    private alleMedlemmer: GruppeMedlemListeModell[] = [];

    constructor(private avhengigheter: MedlemInvitasjonOpprettKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private matDialog: MatDialog) {

        const navnendringer = toSignal(this.skjema.controls.invitasjon.controls.navn.valueChanges);
        this.viserFulltSkjema = computed(() => {
            if (this.visFulltSkjemaHeleTiden()) return true;
            return erIkkeTomStreng(navnendringer());
        });
        //sørger for at så mye som mulig av skjemaet vises når hele skjemaet aktiveres + sørger for at man ikke kan navigere seg ut av skjema uforvarende
        effect(() => {
            if (this.viserFulltSkjema()) {
                if (!this.skjemaelement)
                    window.setTimeout(() => scrollTilTopp(this.skjemaelement.nativeElement, 0), 200);
                else
                    scrollTilTopp(this.skjemaelement.nativeElement, 100);
                this.navigasjonDeaktivering.deaktiverNavigasjon();
            } else {
                this.navigasjonDeaktivering.reaktiverNavigasjon();
            }
        });

        effect(() => {
            this.avhengigheter.finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil(this.gruppe().id).then(
              antall => {
                  this.antallMoeterDetSkalSendesInnkallingTilMoeter.set(antall);
                  antall > 0 ? this.skjema.controls.sendInnkallingTilMoeter.enable() : this.skjema.controls.sendInnkallingTilMoeter.disable();
              }
            );
        });

        //'send invitasjon e-post' togle er disablet når skjemaet er ugyldig
        this.skjema.controls.invitasjon.controls.epost.statusChanges.subscribe(status =>
          status == 'VALID' ? this.skjema.controls.sendInvitasjon.enable() : this.skjema.controls.sendInvitasjon.disable());

        //'send innkalling til møter' togle er disablet når man ikke skal legge til i nye møter.
        this.skjema.controls.oppdaterMoetemal.valueChanges.subscribe(oppdaterMøtemal =>
          oppdaterMøtemal && this.antallMoeterDetSkalSendesInnkallingTilMoeter() > 0 ? this.skjema.controls.sendInnkallingTilMoeter.enable() : this.skjema.controls.sendInnkallingTilMoeter.disable());


        //venter med å laste alle gruppemedlemmer (brukes i validering av navn/epost) til fullt skjema vises
        toObservable(this.viserFulltSkjema)
          .pipe(
            filter(viserFulltSkjema => viserFulltSkjema === true),
            take(1),
            concatMap(() => this.avhengigheter.medlemmer(this.gruppe().id)),
            takeUntilDestroyed()
          )
          .subscribe(medlemmer => this.alleMedlemmer = medlemmer);

    }

    giFokusTilNyttMedlemFelt() {
        const tittelFelt: HTMLInputElement = this.skjemaelement.nativeElement.querySelector('.nyttmedlem-kontainer input[name="navn"]')!;
        tittelFelt.focus();
    }

    async lagre() {
        await this.lagrer.utfør(async () => {
            const spesifikasjon = this.somSpesifikasjon();
            await this.avhengigheter.registrer(spesifikasjon);
            if (spesifikasjon.sendInvitasjon)
                this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjon sendt til {epost}'), {epost: spesifikasjon.epost}));
            else
                this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Ny invitasjon registrert')));
            this.resetSkjema(); //fører til at deaktiverVisFulltSkjema() blir kalt
            this.navigasjonDeaktivering.reaktiverNavigasjon();
            this.vedLagret.emit();
        });
    }

    avbryt() {
        this.resetSkjema();
        this.navigasjonDeaktivering.reaktiverNavigasjon();
        this.vedAvbryt.emit();
    }

    visImporterMedlemmerDialog() {
        this.resetSkjema();
        MedlemImporterDialogKomponent.vis(this.matDialog, this.gruppe());
    }

    //MedlemInvitasjonSkjemaKomponentAvhengigheter implementasjon

    medlemsroller(): Observable<MedlemsRolleModell[]> {
        return this.avhengigheter.medlemsroller(this.gruppe().id);
    }

    verifiserAtEpostIkkeAlleredeErRegistrert(epost: string): ValiderEpostRespons {
        return eksistererMedlemMedEpost(this.alleMedlemmer, epost);
    }

    /** Sann dersom det ikke finnes et medlem med samme navn (casing ignorert), ignorer permanent deaktiverte medlemmer */
    verifiserAtNavnIkkeAlleredeErRegistrert(navn: string): boolean {
        return eksistererMedlemMedNavn(medlemmerIkkeAnonymisert(this.alleMedlemmer), navn);
    }

    //private hjelpemetoder

    private somSpesifikasjon(): OpprettGruppeInvitasjonSpesifikasjon {
        const verdier = this.skjema.getRawValue();
        return {
            gruppeId: this.gruppe().id,

            navn: verdier.invitasjon!.navn,
            epost: verdier.invitasjon!.epost,
            mobilnummer: verdier.invitasjon!.mobilnummer,
            rolleId: verdier.invitasjon!.rolle!.id,
            rolleStartdato: skrivPlainDateSerialisert(verdier.invitasjon!.rolleStartdato!),
            tilgangsroller: MedlemInvitasjonSkjemaKomponent.lesTilgangsroller(this.skjema.controls.invitasjon),
            oppdaterMoetemal: verdier.oppdaterMoetemal,
            skalSignereSomStandard: verdier.skalSignereSomStandard,

            sendInvitasjon: verdier.sendInvitasjon,
            sendInnkallingTilMoeter: verdier.sendInnkallingTilMoeter,
            oppdaterFremtidigeMoeter: verdier.oppdaterMoetemal
        }
    }

    private resetSkjema() {
        this.skjema.reset();
        this.skjema.markAsPristine();
        this.skjema.markAsUntouched();
        fjernAlleFeilFraFormGroup(this.skjema);
    }
}


@Injectable()
export class MedlemInvitasjonOpprettKomponentAvhengigheter {

    constructor(private gruppeRessurs: GruppeRessursClient, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private møteRessurs: MøteRessursClient) {
    }

    /** Returnerer tilgjengelige medlemsroller */
    medlemsroller(gruppeId: GruppeId): Observable<MedlemsRolleModell[]> {
        return this.gruppeRessurs.finnTilgjengeligeRoller(gruppeId).somObservable();
    }

    /** Returnerer alle medlemmer tilknyttet gruppen */
    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilknyttet(gruppeId);
    }

    async registrer(spesifikasjon: OpprettGruppeInvitasjonSpesifikasjon): Promise<void> {
        await this.gruppemedlemDatatjeneste.inviterNyttMedlem(spesifikasjon);
    }

    async finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil(gruppeId: GruppeId): Promise<number> {
        const møter = await this.møteRessurs.finnIkkeAvholdteMøterSomNyeMedlemmmerKanLeggesTil({gruppeId:gruppeId}).somPromise();
        return møter.filter(m => m.moeteInnkallingSendt).length;
    }

}
